import { AxiosResponse } from "axios";
import { format, isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import numeral from "numeral";
import { z } from "zod";

export const getSubdomain = () => {
  const parts = window.location.hostname.split(".");

  return parts[0];
};

export const getDomain = ({ includePort = false } = {}) => {
  const hostname = includePort
    ? window.location.host
    : window.location.hostname;

  return hostname.split(".").slice(-2).join(".");
};

export const redirectToSubdomain = (
  subdomain: string,
  path = window.location.pathname + window.location.search
) => {
  const domain = getDomain({ includePort: true });
  window.location.href = `https://${subdomain}.${domain}${path}`;
};

export const flattenNestedObjectWithBrackets = (
  obj: Record<string, any> = {},
  label = ""
) => {
  return Object.keys(obj).reduce(
    (base, key) => {
      base[`${label}[${key}]`] = obj[key];
      return base;
    },
    {} as Record<string, any>
  );
};

export const capitalizeText = (text = "") => {
  const wordsArray = text.split(" ");

  return wordsArray
    .map(word => `${word[0].toUpperCase()}${word.slice(1)}`)
    .join(" ");
};

export const convertDateFormatToFNS = (format: string): string =>
  format
    .split("")
    .map(letter => {
      if (letter === "m") return "M";
      if (letter === "D") return "d";
      if (letter === "Y") return "y";
      return letter;
    })
    .join("");

export const roundNumber = (num: number, places: number): number => {
  return +(Math.round(Number(`${num}e+${places}`)) + "e-" + places);
};

export const componentLazyImport = async (importFn: () => Promise<any>) => {
  try {
    return await importFn();
  } catch (error) {
    window.location.reload();
  }
};

export const getFileInfoFromURL = (
  url: string
): { name: string; type: string } => {
  try {
    const parsedURL = new URL(url);

    const pathnameParts = parsedURL.pathname.split("/");

    const name = pathnameParts.at(-1) || "";
    const type = name.split(".").at(-1) || "";

    return { name, type };
  } catch (error) {
    const name = url.split("/").at(-1) || "";
    const type = name.split(".").at(-1) || "";

    return { name, type };
  }
};

export const safeFileName = (filename: string): string =>
  filename.replace(/[^a-zA-Z0-9.\-_]/g, "_");

export const isImageExtension = (extension: string): boolean => {
  const imageExtensions = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "webp",
    "bmp",
    "svg",
    "tiff",
    "tif",
    "ico",
    "heif",
    "heic"
  ];

  return imageExtensions.includes(extension);
};

export const getUniqueObjectsArray = <T = Record<string, unknown>>(
  arr: T[],
  key: keyof T
): T[] => {
  const map = new Map(arr.map(item => [item[key], item]));

  return Array.from(map.values());
};

export const downloadBlobResponse = (
  response: AxiosResponse,
  filename: string,
  extension: string
): void => {
  const blob = new Blob([response.data], {
    type: response.headers["Content-Type"] as string
  });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = `${filename.replaceAll(" ", "_")}.${extension}`;
  link.click();
  link.parentNode?.removeChild(link);
};

export const getIsoDate = (date: Date, offset = 12) => {
  const isoDate =
    format(date, "yyyy-MM-dd") + `T${numeral(offset).format("00")}:00:00.000Z`;

  return new Date(isoDate);
};

export const formatDate = (args: {
  datestring?: string | number | null;
  dateFormat: string;
  fallback?: string;
}): string => {
  const { datestring, dateFormat, fallback } = args;

  if (!datestring || !isValid(new Date(datestring))) return fallback || "";

  const date = new Date(datestring);

  const isoDate =
    formatInTimeZone(date, "UTC", "yyyy-MM-dd") + "T12:00:00.000Z";

  return format(new Date(isoDate), dateFormat);
};

export const numberSchema = z.preprocess(
  val => {
    if (typeof val === "string") {
      return val.length > 0 ? Number(val) : undefined;
    }

    return val;
  },
  z.number({ required_error: "Field is required" })
);

export const slugify = (text: string, separator = "_"): string =>
  text
    .toLowerCase()
    .replaceAll(/[^a-zA-Z0-9_]/g, " ")
    .trim()
    .replaceAll(/\s+/g, separator);

export const checkActiveLink = (url: string, regex?: RegExp): boolean => {
  if (regex) {
    return regex.test(window.location.pathname);
  }

  return window.location.pathname.startsWith(url.split("?")[0]);
};

export const base64ToFile = (
  base64String: string,
  filename: string,
  mimeType: string
) => {
  // Strip the Data URL prefix if it exists (e.g., "data:image/png;base64,")
  const pureBase64 = base64String.split(",")[1] || base64String;

  // Decode base64 to raw binary data held in a string
  const byteString = atob(pureBase64);

  // Create an ArrayBuffer with a size matching the binary data length
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  // Insert each byte into the typed array
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  // Return a new File object
  return new File([ab], filename, { type: mimeType });
};
